import { useState } from 'react';
import {
  Building2,
  FileText,
  FolderOpen,
  Shield,
  Truck,
  Users,
} from 'lucide-react';
import { CONTRACT_STATUS, TRANSPORTER_PARTNERS } from './mockTransportData';

function ContractBadge({ status }) {
  const config = CONTRACT_STATUS[status];
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ${config.className}`}
    >
      {config.label}
    </span>
  );
}

function DocumentModal({ partner, onClose }) {
  const docs = [
    'Trade License Copy',
    'VAT TRN Certificate',
    'Third-Party Liability Insurance',
    'Hazmat Transport Permit',
    'Fleet Registration Schedule',
    'Driver Roster & Emirates IDs',
    'Signed SLA / Contract',
    'Bank Guarantee Letter',
  ].slice(0, partner.documentsCount);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-logi-silver/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-logi-grid bg-white p-6 shadow-2xl">
        <h3 className="flex items-center gap-2 text-lg font-bold text-logi-text">
          <FolderOpen className="h-5 w-5 text-logi-accent" />
          Documents — {partner.name}
        </h3>
        <p className="mt-1 text-sm text-logi-muted">{partner.documentsCount} files on record</p>
        <ul className="mt-4 space-y-2">
          {docs.map((doc) => (
            <li
              key={doc}
              className="flex items-center gap-2 rounded-lg border border-logi-grid/80 bg-white/80 px-3 py-2 text-sm text-logi-muted"
            >
              <FileText className="h-4 w-4 text-logi-text0" />
              {doc}
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={onClose}
          className="mt-5 w-full rounded-xl bg-white py-2.5 text-sm font-semibold text-logi-muted ring-1 ring-logi-grid transition hover:bg-logi-silver/80"
        >
          Close
        </button>
      </div>
    </div>
  );
}

export default function TransporterMasterLedger({ partners = TRANSPORTER_PARTNERS }) {
  const [docPartner, setDocPartner] = useState(null);
  const totalFleet = partners.reduce((s, p) => s + p.fleetSize, 0);
  const totalDrivers = partners.reduce((s, p) => s + p.driversCount, 0);

  return (
    <>
      <section className="overflow-hidden rounded-xl border border-logi-grid bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-logi-grid px-4 py-3">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-blue-700" strokeWidth={1.75} />
            <h3 className="text-sm font-bold text-logi-text">Transporter Master Ledger</h3>
          </div>
          <div className="flex gap-3 text-xs text-logi-text0">
            <span>
              <Truck className="mr-1 inline h-3.5 w-3.5 text-logi-accent" />
              {totalFleet} tankers managed
            </span>
            <span>
              <Users className="mr-1 inline h-3.5 w-3.5 text-logi-accent" />
              {totalDrivers} drivers
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-left text-sm">
            <thead>
              <tr className="border-b border-logi-grid/80 bg-white text-[10px] uppercase tracking-wider text-logi-text0">
                <th className="px-4 py-2.5 font-semibold">Transporter Name</th>
                <th className="px-4 py-2.5 font-semibold">Fleet Size</th>
                <th className="px-4 py-2.5 font-semibold">Drivers</th>
                <th className="px-4 py-2.5 font-semibold">Contract Status</th>
                <th className="px-4 py-2.5 font-semibold">Insurance Validity</th>
                <th className="px-4 py-2.5 font-semibold">Emirate</th>
                <th className="px-4 py-2.5 font-semibold">Documents</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-logi-grid/30">
              {partners.map((p) => (
                <tr key={p.id} className="transition-colors hover:bg-logi-silver">
                  <td className="px-4 py-2.5">
                    <p className="font-semibold text-logi-text">{p.name}</p>
                    <p className="font-mono text-[10px] text-logi-text0">
                      {p.id} · {p.tradeLicense}
                    </p>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-1 font-mono text-sm font-semibold text-logi-accent">
                      <Truck className="h-3.5 w-3.5" />
                      {p.fleetSize}
                    </span>
                    <p className="text-[10px] text-logi-text0">tankers</p>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-sm text-logi-muted">
                    {p.driversCount}
                  </td>
                  <td className="px-4 py-2.5">
                    <ContractBadge status={p.contractStatus} />
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-1 text-xs text-logi-muted">
                      <Shield className="h-3.5 w-3.5 text-emerald-700/70" />
                      {p.insuranceValidUntil}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-logi-muted">{p.emirate}</td>
                  <td className="px-4 py-2.5">
                    <button
                      type="button"
                      onClick={() => setDocPartner(p)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-logi-grid bg-logi-silver/80 px-2.5 py-1.5 text-[11px] font-semibold text-logi-muted transition hover:border-logi-accent/40 hover:bg-logi-accent/10 hover:text-logi-accent"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      View ({p.documentsCount})
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {docPartner && (
        <DocumentModal partner={docPartner} onClose={() => setDocPartner(null)} />
      )}
    </>
  );
}
